import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  TbGripVertical, TbArrowUp, TbArrowDown, TbCopy, TbTrash, TbEdit, TbPlus,
  TbDeviceFloppy, TbDeviceDesktop, TbDeviceMobile, TbCode, TbBookmark
} from 'react-icons/tb';
import api from '../../services/server-api/api';
import {
  PREDEFINED_BLOCKS, BRAND_PLACEHOLDERS, CONTENT_PLACEHOLDERS, SAMPLE_CONTENT,
  validateBlockHtml, renderBlocksEmail, createBlocksStructure
} from '../../html-versions/email-blocks';
import './BlockEmailBuilder.css';

const DRAG_TYPE = 'application/x-email-block';
const CUSTOM_BLOCK_TEMPLATE = `<tr>
  <td style="padding:20px 30px;font-family:Arial,Helvetica,sans-serif;font-size:16px;color:#333333;">
    Custom content
  </td>
</tr>`;

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const getError = (error, fallback) => error.response?.data?.message || error.response?.data?.error || fallback;

const BlockEmailBuilder = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const templateFromState = location.state?.template;

  const [templateId, setTemplateId] = useState(templateFromState?._id || null);
  const [templateName, setTemplateName] = useState(templateFromState?.name || '');
  const [blocks, setBlocks] = useState(templateFromState?.structure?.blocks || []);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState('');
  const [savedBlocks, setSavedBlocks] = useState([]);
  const [previewDevice, setPreviewDevice] = useState('desktop');

  const [dropIndex, setDropIndex] = useState(null);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const codeRef = useRef(null);

  const notify = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3500);
  };

  useEffect(() => {
    api.get('/brand/list')
      .then(res => {
        setBrands(res.data || []);
        if (res.data?.length) setBrandId(res.data[0]._id);
      })
      .catch(err => console.error('Error fetching brands:', err));
    fetchSavedBlocks();
  }, []);

  const fetchSavedBlocks = () => {
    api.get('/api/email-blocks/list')
      .then(res => setSavedBlocks(res.data || []))
      .catch(err => console.error('Error fetching saved blocks:', err));
  };

  const selectedBrand = brands.find(b => b._id === brandId);
  const previewBrand = selectedBrand
    ? { ...selectedBrand, logoLink: selectedBrand.langs?.[0]?.logoLink }
    : {};

  const previewHtml = useMemo(
    () => renderBlocksEmail(createBlocksStructure(blocks), previewBrand, SAMPLE_CONTENT),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [blocks, brandId, brands]
  );

  // --- Block list operations ---

  const insertBlock = (block, index = blocks.length) => {
    const copy = { id: newId(), type: block.type || 'custom', name: block.name, html: block.html };
    setBlocks(prev => [...prev.slice(0, index), copy, ...prev.slice(index)]);
    setSelectedIndex(index);
  };

  const moveBlock = (from, to) => {
    if (to < 0 || to >= blocks.length || from === to) return;
    setBlocks(prev => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setSelectedIndex(to);
  };

  const duplicateBlock = (index) => insertBlock(blocks[index], index + 1);

  const removeBlock = (index) => {
    setBlocks(prev => prev.filter((_, i) => i !== index));
    setSelectedIndex(null);
  };

  // --- Drag & drop (library -> canvas, and reordering inside the canvas) ---

  const handleDragStart = (e, payload) => {
    e.dataTransfer.setData(DRAG_TYPE, JSON.stringify(payload));
    e.dataTransfer.effectAllowed = payload.source === 'canvas' ? 'move' : 'copy';
  };

  const handleBlockDragOver = (e, index) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropIndex(e.clientY < rect.top + rect.height / 2 ? index : index + 1);
  };

  const handleCanvasDragOver = (e) => {
    e.preventDefault();
    if (e.target === e.currentTarget) setDropIndex(blocks.length);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData(DRAG_TYPE);
    const target = dropIndex ?? blocks.length;
    setDropIndex(null);
    if (!raw) return;

    const payload = JSON.parse(raw);
    if (payload.source === 'canvas') {
      // Removing the dragged block shifts the target when moving down
      moveBlock(payload.index, payload.index < target ? target - 1 : target);
    } else {
      insertBlock(payload.block, target);
    }
  };

  // --- Block code editor ---

  const openEditor = (index) => {
    const block = index === null ? { name: 'Custom block', html: CUSTOM_BLOCK_TEMPLATE } : blocks[index];
    setEditor({ index, name: block.name, html: block.html, saveToLibrary: false });
  };

  const editorError = editor ? validateBlockHtml(editor.html) : null;

  const insertPlaceholder = (placeholder) => {
    const textarea = codeRef.current;
    if (!textarea) return;
    const { selectionStart, selectionEnd } = textarea;
    const html = editor.html.slice(0, selectionStart) + placeholder + editor.html.slice(selectionEnd);
    setEditor(prev => ({ ...prev, html }));
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = selectionStart + placeholder.length;
    });
  };

  const applyEditor = async () => {
    if (editorError) return;
    const name = editor.name.trim() || 'Custom block';
    const html = editor.html.trim();

    if (editor.saveToLibrary) {
      try {
        await api.post('/api/email-blocks/create', { name, html });
        fetchSavedBlocks();
      } catch (err) {
        notify(getError(err, 'Failed to save the block'), 'error');
        return;
      }
    }

    if (editor.index === null) {
      insertBlock({ type: 'custom', name, html });
    } else {
      setBlocks(prev => prev.map((b, i) => (i === editor.index ? { ...b, name, html } : b)));
    }
    setEditor(null);
  };

  const deleteSavedBlock = async (block) => {
    if (!window.confirm(`Delete "${block.name}" from My blocks?`)) return;
    try {
      await api.delete(`/api/email-blocks/${block._id}`);
      fetchSavedBlocks();
    } catch (err) {
      notify(getError(err, 'Failed to delete the block'), 'error');
    }
  };

  // --- Save template ---

  const handleSave = async () => {
    if (!templateName.trim()) {
      notify('Enter a template name', 'error');
      return;
    }
    if (blocks.length === 0) {
      notify('Add at least one block', 'error');
      return;
    }
    const invalid = blocks.findIndex(b => validateBlockHtml(b.html));
    if (invalid >= 0) {
      setSelectedIndex(invalid);
      notify(`Block ${invalid + 1} (${blocks[invalid].name}): ${validateBlockHtml(blocks[invalid].html)}`, 'error');
      return;
    }

    setSaving(true);
    const payload = { name: templateName.trim(), structure: createBlocksStructure(blocks) };
    try {
      if (templateId) {
        await api.put(`/api/email-templates/update/${templateId}`, payload);
      } else {
        const res = await api.post('/api/email-templates/create', payload);
        setTemplateId(res.data.template._id);
      }
      notify('Template saved');
    } catch (err) {
      notify(getError(err, 'Failed to save the template'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const editorPreviewHtml = editor && !editorError
    ? renderBlocksEmail(createBlocksStructure([{ html: editor.html }]), previewBrand, SAMPLE_CONTENT)
    : '';

  return (
    <div className="block-builder">
      <div className="block-builder-toolbar">
        <input
          className="block-builder-name"
          placeholder="Template name"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
        />
        <label className="block-builder-brand">
          Brand
          <select value={brandId} onChange={(e) => setBrandId(e.target.value)}>
            {brands.map(b => <option key={b._id} value={b._id}>{b.brandName}</option>)}
          </select>
        </label>
        <div className="block-builder-toolbar-spacer" />
        <button className="bb-btn" onClick={() => navigate('/email-templates/list')}>Templates</button>
        <button className="bb-btn primary" onClick={handleSave} disabled={saving}>
          <TbDeviceFloppy /> {saving ? 'Saving...' : 'Save template'}
        </button>
      </div>

      <div className="block-builder-body">
        {/* Library */}
        <aside className="block-library">
          <h4>Blocks</h4>
          <p className="block-library-hint">Drag into the template or click to add at the end.</p>
          {PREDEFINED_BLOCKS.map(block => (
            <div
              key={block.type}
              className="block-library-item"
              draggable
              onDragStart={(e) => handleDragStart(e, { source: 'library', block })}
              onClick={() => insertBlock(block)}
            >
              <TbGripVertical /> {block.name}
            </div>
          ))}

          <h4>My blocks</h4>
          {savedBlocks.length === 0 && <p className="block-library-hint">Custom blocks you save appear here.</p>}
          {savedBlocks.map(block => (
            <div
              key={block._id}
              className="block-library-item saved"
              draggable
              onDragStart={(e) => handleDragStart(e, { source: 'library', block: { type: 'custom', name: block.name, html: block.html } })}
              onClick={() => insertBlock({ type: 'custom', name: block.name, html: block.html })}
            >
              <TbBookmark /> <span>{block.name}</span>
              <button
                className="block-library-delete"
                onClick={(e) => { e.stopPropagation(); deleteSavedBlock(block); }}
                title="Delete"
              >
                <TbTrash />
              </button>
            </div>
          ))}

          <button className="bb-btn block-library-custom" onClick={() => openEditor(null)}>
            <TbCode /> Custom block
          </button>
        </aside>

        {/* Template structure */}
        <section
          className={`block-canvas ${dropIndex === blocks.length ? 'drop-end' : ''}`}
          onDragOver={handleCanvasDragOver}
          onDragLeave={(e) => { if (e.target === e.currentTarget) setDropIndex(null); }}
          onDrop={handleDrop}
        >
          {blocks.length === 0 && (
            <div className="block-canvas-empty">
              <TbPlus />
              <p>Drag blocks here to build the template</p>
            </div>
          )}

          {blocks.map((block, index) => {
            const error = validateBlockHtml(block.html);
            return (
              <div
                key={block.id}
                className={[
                  'block-card',
                  selectedIndex === index ? 'selected' : '',
                  error ? 'invalid' : '',
                  dropIndex === index ? 'drop-before' : '',
                  dropIndex === index + 1 && index === blocks.length - 1 ? 'drop-after' : ''
                ].join(' ')}
                draggable
                onDragStart={(e) => handleDragStart(e, { source: 'canvas', index })}
                onDragOver={(e) => handleBlockDragOver(e, index)}
                onDragEnd={() => setDropIndex(null)}
                onClick={() => setSelectedIndex(index)}
              >
                <TbGripVertical className="block-card-handle" />
                <div className="block-card-info">
                  <strong>{block.name}</strong>
                  {error
                    ? <small className="block-card-error">{error}</small>
                    : <small>{block.type === 'custom' ? 'Custom HTML' : 'Predefined'}</small>}
                </div>
                <div className="block-card-actions" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => moveBlock(index, index - 1)} disabled={index === 0} title="Move up"><TbArrowUp /></button>
                  <button onClick={() => moveBlock(index, index + 1)} disabled={index === blocks.length - 1} title="Move down"><TbArrowDown /></button>
                  <button onClick={() => openEditor(index)} title="Edit HTML"><TbEdit /></button>
                  <button onClick={() => duplicateBlock(index)} title="Duplicate"><TbCopy /></button>
                  <button className="danger" onClick={() => removeBlock(index)} title="Remove"><TbTrash /></button>
                </div>
              </div>
            );
          })}
        </section>

        {/* Live preview */}
        <section className="block-preview">
          <div className="block-preview-toolbar">
            <span>Preview{selectedBrand ? ` · ${selectedBrand.brandName}` : ''}</span>
            <div>
              <button className={previewDevice === 'desktop' ? 'active' : ''} onClick={() => setPreviewDevice('desktop')} title="Desktop"><TbDeviceDesktop /></button>
              <button className={previewDevice === 'mobile' ? 'active' : ''} onClick={() => setPreviewDevice('mobile')} title="Mobile"><TbDeviceMobile /></button>
            </div>
          </div>
          <div className="block-preview-frame">
            <iframe
              title="Email preview"
              srcDoc={previewHtml}
              style={{ width: previewDevice === 'mobile' ? 375 : '100%' }}
            />
          </div>
        </section>
      </div>

      {/* Block code editor */}
      {editor && (
        <div className="modal-overlay">
          <div className="modal-content block-editor">
            <button className="modal-close-button" onClick={() => setEditor(null)}>✕</button>
            <h3>{editor.index === null ? 'Add custom block' : 'Edit block'}</h3>

            <div className="block-editor-grid">
              <div>
                <label className="block-editor-label">Name</label>
                <input
                  className="block-editor-input"
                  value={editor.name}
                  onChange={(e) => setEditor(prev => ({ ...prev, name: e.target.value }))}
                />

                <label className="block-editor-label">
                  HTML <small>— must be one or more complete &lt;tr&gt;…&lt;/tr&gt; rows</small>
                </label>
                <textarea
                  ref={codeRef}
                  className={`block-editor-code ${editorError ? 'invalid' : ''}`}
                  value={editor.html}
                  spellCheck={false}
                  onChange={(e) => setEditor(prev => ({ ...prev, html: e.target.value }))}
                />
                <div className={`block-editor-status ${editorError ? 'error' : 'ok'}`}>
                  {editorError || 'Valid row HTML'}
                </div>

                <div className="block-editor-placeholders">
                  <span>Brand:</span>
                  {BRAND_PLACEHOLDERS.map(p => (
                    <button key={p.value} type="button" onClick={() => insertPlaceholder(p.value)} title={p.value}>{p.label}</button>
                  ))}
                </div>
                <div className="block-editor-placeholders">
                  <span>Content:</span>
                  {CONTENT_PLACEHOLDERS.map(p => (
                    <button key={p.value} type="button" onClick={() => insertPlaceholder(p.value)} title={p.value}>{p.label}</button>
                  ))}
                </div>
              </div>

              <div className="block-editor-preview">
                {editorPreviewHtml
                  ? <iframe title="Block preview" srcDoc={editorPreviewHtml} />
                  : <div className="block-editor-preview-empty">Fix the HTML to see the preview</div>}
              </div>
            </div>

            <div className="block-editor-footer">
              <label className="block-editor-save">
                <input
                  type="checkbox"
                  checked={editor.saveToLibrary}
                  onChange={(e) => setEditor(prev => ({ ...prev, saveToLibrary: e.target.checked }))}
                />
                Also save to My blocks
              </label>
              <button className="bb-btn" onClick={() => setEditor(null)}>Cancel</button>
              <button className="bb-btn primary" onClick={applyEditor} disabled={!!editorError}>
                {editor.index === null ? 'Add block' : 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}

      {notification && (
        <div className={`block-builder-notification ${notification.type}`}>{notification.message}</div>
      )}
    </div>
  );
};

export default BlockEmailBuilder;
